import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PromptInputForAssessment from '../common/ui/PromptInputForAssessment';
import api from '../../services/api';
import { jobService } from '../../services/job.service';
import { toast } from 'react-toastify';
import AudioInterviewStage from '../audio/AudioInterviewStage';
import AgentInterviewStage from '../audio/AgentInterviewStage';

const MAX_DURATION_MINUTES = 20;

const FinalInterviewTest = () => {
    const params = useParams();
    const { jobId, candidateId } = params;
    const navigate = useNavigate();

    // Chat conversation state
    const [conversationHistory, setConversationHistory] = useState([]);
    const [userAnswer, setUserAnswer] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [finalResult, setFinalResult] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [questionCount, setQuestionCount] = useState(0);
    const [maxQuestions] = useState(20); // Updated to 20 questions
    const [timeRemaining, setTimeRemaining] = useState(MAX_DURATION_MINUTES * 60); // seconds
    const [timerStarted, setTimerStarted] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [modality, setModality] = useState('audio');
    const [firstAiLine, setFirstAiLine] = useState('');
    const timerRef = useRef(null);

    // Start interview by calling the final interview API
    const startInterview = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.post(`/public/final-interview/start`, { jobId, candidateId });
            if (response.data.success) {
                const { question, sessionId, timeRemaining: apiTimeRemaining, modality: m } = response.data.data;
                if (m && m !== 'inherit') setModality(m);
                setFirstAiLine(question || '');
                setSessionId(sessionId);
                setConversationHistory([{
                    type: 'question',
                    text: question,
                    timestamp: new Date()
                }]);
                setQuestionCount(1);
                setTimeRemaining((apiTimeRemaining || MAX_DURATION_MINUTES) * 60);
                setTimerStarted(true); // Start the timer
                toast.success('Final interview started!');
            } else {
                toast.error(response.data.message || 'Failed to start final interview.');
            }
        } catch (error) {
            console.error("Error starting final interview:", error);
            toast.error("Failed to start final interview. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [jobId, candidateId]);

    useEffect(() => {
        startInterview();
        // eslint-disable-next-line
    }, [jobId, candidateId]);

    // Timer logic
    useEffect(() => {
        if (isCompleted || isLoading || !timerStarted) return;
        
        if (timeRemaining <= 0) {
            handleAutoSubmit();
            return;
        }
        
        timerRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    handleAutoSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        
        return () => clearInterval(timerRef.current);
    }, [isCompleted, isLoading, timeRemaining, timerStarted]);

    // Proctoring: Prevent tab changes and browser navigation
    useEffect(() => {
        if (!timerStarted || isCompleted) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = 'Are you sure you want to leave? Your interview progress will be lost.';
            return e.returnValue;
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                toast.warning('⚠️ Please stay on this tab during the interview. Tab switching is not allowed.');
                // Optionally auto-submit if they switch tabs
                // handleAutoSubmit();
            }
        };

        const handleKeyDown = (e) => {
            // Prevent common keyboard shortcuts for tab switching
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'Tab' || e.key === 't' || e.key === 'w') {
                    e.preventDefault();
                    toast.warning('⚠️ Tab switching is not allowed during the interview.');
                }
            }
        };

        // Add event listeners
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [timerStarted, isCompleted]);

    // Fetch job and company information
    useEffect(() => {
        const fetchJobInfo = async () => {
            try {
                const response = await jobService.getJobTitle(jobId);
                if (response.companyName) {
                    setCompanyName(response.companyName);
                }
            } catch (error) {
                console.error('Error fetching job info:', error);
            }
        };
        
        if (jobId) {
            fetchJobInfo();
        }
    }, [jobId]);

    // Auto-submit function
    const handleAutoSubmit = async () => {
        if (isSubmitting || isCompleted) return;
        
        try {
            setIsSubmitting(true);
            toast.info("Time's up! Submitting your interview automatically...");
            
            // Submit final response if there's any pending answer
            if (userAnswer.trim()) {
                await handleSubmitAnswer();
            }
            
            // Force interview completion
            setIsCompleted(true);
            setFinalResult({
                score: 0,
                feedback: 'Interview ended due to time limit'
            });
            
            setTimeout(() => {
                navigate('/test-complete', { 
                    state: { 
                        assessment: { overallScore: 0, feedback: 'Interview ended due to time limit' },
                        type: 'final_interview'
                    }
                });
            }, 2000);
        } catch (error) {
            console.error("Error in auto-submit:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitAnswer = async () => {
        if (!userAnswer.trim() || !sessionId) return;
        try {
            setIsSubmitting(true);
            // Add user's answer to conversation
            const newUserMessage = {
                type: 'answer',
                text: userAnswer,
                timestamp: new Date()
            };
            setConversationHistory(prev => [...prev, newUserMessage]);
            const response = await api.post(`/public/final-interview/respond`, {
                sessionId,
                response: userAnswer
            });
            if (response.data.success) {
                const data = response.data.data;
                if (data.isComplete) {
                    setIsCompleted(true);
                    setFinalResult(data.finalAssessment || { score: null, feedback: 'Interview completed.' });
                    toast.success('Final interview completed!');
                    setTimeout(() => {
                        navigate('/test-complete', {
                            state: {
                                assessment: { overallScore: data.finalAssessment?.overallScore, feedback: data.finalAssessment?.detailedAnalysis },
                                type: 'finalInterview'
                            }
                        });
                    }, 2000);
                } else {
                    setConversationHistory(prev => [...prev, {
                        type: 'question',
                        text: data.question,
                        timestamp: new Date()
                    }]);
                    setQuestionCount(data.questionCount);
                }
                setUserAnswer("");
            } else {
                toast.error(response.data.message || 'Failed to submit answer.');
            }
        } catch (error) {
            console.error("Error submitting answer:", error);
            
            // Handle completed session gracefully
            if (error.response?.data?.message?.includes('completed')) {
                toast.info("Interview has already been completed.");
                // Try to get the final assessment
                try {
                    const sessionResponse = await api.get(`/public/final-interview/session/${sessionId}`);
                    if (sessionResponse.data.success && sessionResponse.data.data.finalAssessment) {
                        setIsCompleted(true);
                        setFinalResult(sessionResponse.data.data.finalAssessment);
                        toast.success('Final interview completed!');
                        setTimeout(() => {
                            navigate('/test-complete', {
                                state: {
                                    assessment: { 
                                        overallScore: sessionResponse.data.data.finalAssessment?.overallScore, 
                                        feedback: sessionResponse.data.data.finalAssessment?.detailedAnalysis 
                                    },
                                    type: 'finalInterview'
                                }
                            });
                        }, 2000);
                    }
                } catch (sessionError) {
                    console.error("Error fetching session details:", sessionError);
                }
            } else {
                toast.error("Failed to submit answer. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };



    // Timer display helper
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-blue-200">
                <div className="text-center">
                    <div className="relative w-20 h-20 mx-auto mb-4">
                        <div className="w-20 h-20 rounded-full border-4 border-gray-200"></div>
                        <div className="absolute top-0 left-0 w-20 h-20 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-gray-600 text-lg">Starting your final interview...</p>
                </div>
            </div>
        );
    }

    if (isCompleted && finalResult) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-green-200">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4 text-center">
                    <div className="text-green-500 text-6xl mb-4">✅</div>
                    <h2 className="text-2xl font-semibold text-green-600 mb-4">
                        Final Interview Completed!
                    </h2>
                    {finalResult?.overallScore !== undefined && (
                        <p className="text-gray-600 mb-4">
                            Score: <span className="font-bold text-green-600">{finalResult?.overallScore || 0}/100</span>
                        </p>
                    )}
                    {finalResult?.feedback && (
                        <p className="text-gray-600 text-sm">
                            {finalResult?.feedback || ''}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen relative bg-gradient-to-b from-white to-blue-200">
            {/* Header */}
            <div className="bg-white shadow-sm border-b px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">
                                {companyName ? `${companyName}.ai` : 'Company.ai'}
                            </h1>
                            <p className="text-gray-600">Final Interview</p>
                        </div>
                        {timerStarted && (
                            <div className={`text-lg font-semibold ${timeRemaining < 300 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                                ⏰ {formatTime(timeRemaining)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Audio mode — ElevenLabs Conversational AI agent (Gemini-driven, low latency) */}
            {modality === 'audio' && !isCompleted && (
              <div className="flex-1 max-w-4xl mx-auto w-full p-6">
                <AgentInterviewStage
                  round="finalInterview"
                  jobId={jobId}
                  resumeId={candidateId}
                  candidateName={companyName || 'there'}
                  onComplete={(result) => {
                    if (result?.aborted) return;
                    setIsCompleted(true);
                    setFinalResult({ score: result?.overallScore, feedback: result?.recommendation });
                    setTimeout(() => navigate('/test-complete', {
                      state: { assessment: { overallScore: result?.overallScore, feedback: result?.recommendation }, type: 'finalInterview' }
                    }), 1800);
                  }}
                />
              </div>
            )}

            {/* Text mode (legacy) */}
            {modality !== 'audio' && (
            <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
                {/* Chat Messages */}
                <div className="flex-1 p-6 overflow-y-auto">
                    <div className="space-y-4 max-w-4xl mx-auto">
                        {conversationHistory.map((msg, index) => (
                            <div key={index} className={`flex ${msg.type === 'answer' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-3xl px-6 py-4 rounded-2xl shadow-sm ${
                                    msg.type === 'answer' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-white text-gray-800'
                                }`}>
                                    <div className="text-sm font-medium mb-2">
                                        {msg.type === 'answer' ? 'You' : companyName || 'Company'}
                                    </div>
                                    <div className="text-base whitespace-pre-wrap leading-relaxed break-words">{msg.text}</div>
                                    <div className="text-xs opacity-70 mt-2">
                                        {msg.timestamp.toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* Loading indicator */}
                        {isSubmitting && (
                            <div className="flex justify-start">
                                <div className="bg-white text-gray-800 px-6 py-4 rounded-2xl shadow-sm max-w-3xl">
                                    <div className="flex items-center gap-3">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                                        <span className="text-sm">Processing your response...</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Input Area */}
                <div className="border-t bg-white p-6">
                    <div className="max-w-4xl mx-auto">
                        <PromptInputForAssessment
                            value={userAnswer}
                            onChange={setUserAnswer}
                            onSubmit={handleSubmitAnswer}
                            loading={isSubmitting}
                            placeholder="Type your response here..."
                            showSaveButton={true}
                            saveButtonText={
                                isCompleted
                                    ? "Completed"
                                    : isSubmitting
                                    ? "Submitting..."
                                    : "Submit Response"
                            }
                            disabled={isSubmitting || isCompleted}
                        />
                    </div>
                </div>
            </div>
            )}

            {/* Completion message */}
            {isCompleted && finalResult && (
                <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t border-green-200">
                    <div className="max-w-7xl mx-auto p-6">
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-green-800 mb-2">
                                Final Interview Completed Successfully!
                            </h3>
                            <p className="text-green-600">
                                {finalResult.score !== null ? `Score: ${finalResult.score}/100` : ''} | 
                                {finalResult.feedback}
                            </p>
                            <p className="text-sm text-gray-600 mt-2">
                                Thank you for your time. We'll review your responses and get back to you soon.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinalInterviewTest; 